

import React, { useEffect, useState } from 'react';
import TrainerDialog from './TrainerDialog';
import EditTrainer from './EditTrainer';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { TrainerManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Trainer } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Trainers/PathItems';
import AISuggestions from '../../components/AISuggestions';



const TrainersList: React.FC = () => {
  const { onError } = useToast();
  const [search, setSearch] = useState('');
  const [filteredTrainers, setFilteredTrainers] = useState<Trainer[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrainerId, setEditingTrainerId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trainerToDelete, setTrainerToDelete] = useState<{ id: string, name: string } | null>(null);

  const searchTrainers = async (query: string) => {
    onError((await Session.getClient().request(TrainerManagment.searchTrainer({ query, limit: 100, offset: 0 })))
      .maybeOk((value) => setFilteredTrainers(value as Trainer[])));
  };

  useEffect(() => {
    searchTrainers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddTrainer = async (name: string) => {
    if (name.trim()) {
      onError((await Session.getClient().request(TrainerManagment.createTrainer({ name: name.trim() })))
        .maybeOk(() => searchTrainers(search)));
    }
    setDialogOpen(false);
  };

  const handleEditClick = (trainer: Trainer) => {
    setEditingTrainerId(trainer.id);
  };

  const handleEditBack = () => {
    searchTrainers(search);
    setEditingTrainerId(null);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setTrainerToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (trainerToDelete) {
      onError((await Session.getClient().addPathParam('trainerId', trainerToDelete.id).request(TrainerManagment.deleteTrainer()))
        .maybeOk(() => searchTrainers(search)));
    }
    setTrainerToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setTrainerToDelete(null);
    setDeleteDialogOpen(false);
  };

  if (editingTrainerId) {
    return (
      <EditTrainer
        id={editingTrainerId}
        onBack={handleEditBack}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search trainers..."
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            searchTrainers(e.target.value);
          }}
          sx={{ width: 260 }}
        />
        <AISuggestions 
          client={Session.getClient()}
          currentSnapshot={JSON.stringify(filteredTrainers)}
          usageInstructions={TrainerManagment.toAgentInstructions()}
          developerMessage={'The user is in the view where they can search and see a list of trainers. For any of the operations ' +
            'regarding a specific trainer the path parameter with the name trainerId MUST be specified with that trainer id.'}
          onOperationsApplied={() => searchTrainers(search)}
        />
        <Button variant="contained" onClick={() => setDialogOpen(true)}>New Trainer</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Experience</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTrainers.map(trainer => (
              <TableRow key={trainer.id}>
                <TableCell>{trainer.name}</TableCell>
                <TableCell>{trainer.description}</TableCell>
                <TableCell>{trainer.experienceInYears}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => handleEditClick(trainer)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(trainer.id, trainer.name)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredTrainers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                  No trainers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TrainerDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleAddTrainer} />
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Trainer?"
        content={`Are you sure you want to delete the trainer "${trainerToDelete?.name}"? This action cannot be undone.`}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default TrainersList;
